import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getUsers, getDailyByEmployeeMonth } from "../../services/newdaily";
import Button from "../../components/MainButton/MainButton";
import MyCalendar from "../../components/Calendar/Calendar";
import TimeBar from "../../components/TimeBar/TimeBar";
import Notes from "../../components/Notes/Notes";
import TaskCard from "../../components/TaskCard/TaskCard";
import TaskHeader from "../../components/TaskHeader/TaskHeader";
import ViewSelector from "../../components/ViewSelector/ViewSelector";
import PlusIcon from "../../assets/svgs/plus.svg";
import FormCheckIcon from "../../assets/svgs/formcheck.svg";
import CloseIcon from "../../assets/svgs/close.svg";
import foldersImg from "../../assets/images/folders.png";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import "overlayscrollbars/overlayscrollbars.css";
import moment from "moment";
import styles from "./Main.module.css";
import TaskCreationModal from "../../modals/TaskCreationModal/TaskCreationModal";
import { SpinnerComponent } from "../../components/Spinner";

const Main = ({ empleado, refreshSignal, isAdmin, onMonthChange }) => {
  const [events, setEvents] = useState([]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    const fechaParam = searchParams.get("date");
    if (fechaParam && moment(fechaParam, "YYYY-MM-DD", true).isValid()) {
      return fechaParam;
    }
    return moment().format("YYYY-MM-DD");
  });
  const [visibleMonthYear, setVisibleMonthYear] = useState(() => {
    const fechaParam = searchParams.get("date");
    const baseDate =
      fechaParam && moment(fechaParam, "YYYY-MM-DD", true).isValid()
        ? moment(fechaParam, "YYYY-MM-DD")
        : moment();

    return {
      month: baseDate.month() + 1,
      year: baseDate.year(),
    };
  });
  const [notificationMessage, setNotificationMessage] = useState("");
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [userName, setUserName] = useState("");
  const [userNotes, setUserNotes] = useState("");
  const [proyectosTodos, setProyectosTodos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedTaskId, setHighlightedTaskId] = useState(null);
  const hasAppliedInitialParams = useRef(false);

  const isPastDate = moment(selectedDate).startOf("day").isBefore(moment().startOf("day"), "day");

  const buildFirstDay = ({ month, year }) =>
    moment({ year, month: month - 1, date: 1 }).format("YYYY-MM-DD");

  const fetchEvents = async () => {

    try {
      if (!empleado) return;
      setIsLoading(true);

      const firstDay = buildFirstDay(visibleMonthYear);

      const data = await getDailyByEmployeeMonth({
        empleado,
        fecha: firstDay,
      });

      setProyectosTodos(Array.isArray(data?.ProyectosTODOS) ? data.ProyectosTODOS : []);

      if (data?.Daily) {
        const tareaDefs = data.Tareas || [];
        const proyectoDefs = data.Proyectos || [];
        const proyectosTodosDefs = data.ProyectosTODOS || [];

        const dailyList = Array.isArray(data?.Daily) ? data.Daily : [];
        const futureList = Array.isArray(data?.future_tasks) ? data.future_tasks : [];

        const mergedTasks = [...dailyList, ...futureList];

        const filteredEvents = mergedTasks
          .filter((task) => task.Automatica !== 1)
          .map((task) => {
            const displayStart = task.Inicio
              ? moment(`${task.Fecha} ${task.Inicio}`, "YYYY-MM-DD HH:mm:ss").toDate()
              : moment(task.Fecha, "YYYY-MM-DD").startOf("day").toDate();

            const sortStart = task.Inicio ? displayStart : new Date("2999-12-31");

            const endDate = task.Inicio
              ? moment(displayStart).add(task.Minutos || 0, "minutes").toDate()
              : displayStart;

            let tareaName = task.Tarea || "";
            if (task.IDTarea) {
              const foundTarea = tareaDefs.find(
                (t) => t.id.toString() === task.IDTarea.toString()
              );
              if (foundTarea) tareaName = foundTarea.tarea;
            }

            let subtareaName = task.Subtarea || "";

            if (task.IDTarea && task.IDSubtarea) {
              const foundTarea = tareaDefs.find(
                (t) => t.id.toString() === task.IDTarea.toString()
              );
              if (foundTarea && Array.isArray(foundTarea.subtareas)) {
                const foundSubtarea = foundTarea.subtareas.find(
                  (s) => s.id.toString() === task.IDSubtarea.toString()
                );
                if (foundSubtarea) subtareaName = foundSubtarea.subtarea;
              }
            }

            let proyectoName = task.Proyecto || "";

            if (task.IProyecto) {
              const foundProyecto =
                proyectoDefs.find((p) => p.id.toString() === task.IProyecto.toString()) ||
                proyectosTodosDefs.find((p) => p.id.toString() === task.IProyecto.toString());

              if (foundProyecto?.Proyecto) {
                proyectoName = foundProyecto.Proyecto;
              }
            }

            let taskColor = task.Color || "#ccc";
            if (task.IDTarea) {
              const foundTarea = tareaDefs.find(
                (t) => t.id.toString() === task.IDTarea.toString()
              );
              if (foundTarea && foundTarea.color) {
                taskColor = foundTarea.color.startsWith("#")
                  ? foundTarea.color
                  : `#${foundTarea.color}`;
              }
            }

            return {
              id: task.id,
              title: tareaName,
              start: displayStart,
              sortStart,
              end: endDate,
              color: taskColor,
              date: moment(task.Fecha, "YYYY-MM-DD").format("YYYY-MM-DD"),
              Minutos: task.Minutos || 0,
              Tarea: tareaName,
              Subtarea: subtareaName,
              Proyecto: proyectoName,
              Inicio: task.Inicio || null,
              Hecho: task.Hecho || 0,
              IDOrigen: task.IDOrigen ?? 0,
              Origen: task.Origen || null,
              Empleado: task.Empleado,
              Notas: task.Notas,
              Descripcion: task.Descripcion,
              idTarea: task.IDTarea ?? null,
              idSubtarea: task.IDSubtarea ?? null,
              idProyecto: task.IProyecto ?? null,
              config_json: task.config_json ?? null,
              is_periodic: task.is_periodic ?? false,
              endDate: task.endDate ?? task.end_date ?? null,

              is_projection: !!task.is_projection,
              parent_periodic_id: task.parent_periodic_id ?? null,
            };
          })
          .sort((a, b) => {
            if (a.Hecho !== b.Hecho) return a.Hecho - b.Hecho;
            if (a.sortStart && b.sortStart) {
              const diff = a.sortStart - b.sortStart;
              if (diff !== 0) return diff;
              return a.Tarea.localeCompare(b.Tarea);
            }
            return a.Tarea.localeCompare(b.Tarea);
          });

        setEvents(filteredEvents);

      }
    } catch (error) {
      console.error("❌ Error al cargar tareas:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fechaParam = searchParams.get("date");
    const taskIdParam = searchParams.get("task_id");

    let usedParams = false;

    if (fechaParam && moment(fechaParam, "YYYY-MM-DD", true).isValid()) {
      setSelectedDate(fechaParam);

      const m = moment(fechaParam, "YYYY-MM-DD");
      setVisibleMonthYear({
        month: m.month() + 1,
        year: m.year(),
      });

      usedParams = true;
    }

    if (taskIdParam) {
      setHighlightedTaskId(String(taskIdParam));
      usedParams = true;
    } else {
      setHighlightedTaskId(null);
    }

    if (usedParams && !hasAppliedInitialParams.current) {
      hasAppliedInitialParams.current = true;
      navigate(window.location.pathname, { replace: true });
    }

  }, [searchParams, navigate]);

  useEffect(() => {
    if (!highlightedTaskId || !selectedTasks.length) return;

    const existsInSelectedDate = selectedTasks.some(
      (task) => String(task.id) === String(highlightedTaskId)
    );

    if (!existsInSelectedDate) return;

    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-task-id="${highlightedTaskId}"]`);
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [highlightedTaskId, selectedTasks]);

  useEffect(() => {
    if (!empleado) return;

    const fetchUser = async () => {
      try {
        const data = await getUsers();

        if (data?.Usuarios && Array.isArray(data.Usuarios)) {
          const user = data.Usuarios.find(
            (u) => u.id.toString() === empleado.toString()
          );

          if (user) {
            setUserName(user.Nombre);
            setUserNotes(user.Notas ?? "");
          }
        }
      } catch (error) {
        console.error("Error al obtener nombre de usuario:", error);
      }
    };

    fetchUser();
  }, [empleado]);

  useEffect(() => {
    if (modalIsOpen) return;
    fetchEvents();
  }, [refreshSignal, visibleMonthYear, modalIsOpen]);

  useEffect(() => {
    const filteredTasks = events.filter((task) => task.date === selectedDate);
    setSelectedTasks(filteredTasks);
  }, [events, selectedDate]);

  useEffect(() => {
    if (modalIsOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [modalIsOpen]);


  const updateTaskInState = (taskId, newHecho) => {
    setEvents((prevEvents) =>
      prevEvents.map((task) =>
        task.id === taskId ? { ...task, Hecho: newHecho } : task
      )
    );
    setSelectedTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.id === taskId ? { ...task, Hecho: newHecho } : task
      )
    );
  };

  const handleDayClick = (_, date) => {
    const formattedDate = moment(date).format("YYYY-MM-DD");
    setSelectedDate(formattedDate);
  };

  const sortedSelectedTasks = [...selectedTasks].sort((a, b) => {
    if (a.Hecho !== b.Hecho) return a.Hecho - b.Hecho;

    const hasOrigenA = a.Origen !== null && a.Origen !== "" && a.Origen !== userName;
    const hasOrigenB = b.Origen !== null && b.Origen !== "" && b.Origen !== userName;
    if (hasOrigenA !== hasOrigenB) return hasOrigenB - hasOrigenA;

    if (a.sortStart && b.sortStart) {
      const diff = a.sortStart - b.sortStart;
      if (diff !== 0) return diff;
    }

    if (a.Minutos !== b.Minutos) return b.Minutos - a.Minutos;

    return a.Tarea.localeCompare(b.Tarea);
  });

  const handleTaskSuccess = (message) => {
    setNotificationMessage(message);
    setTimeout(() => {
      setNotificationMessage("");
    }, 3000);
    fetchEvents();
  };

  const handleCloseBanner = () => {
    setNotificationMessage("");
  };

  const handleVisibleMonthChange = ({ month, year }) => {
    setVisibleMonthYear({ month, year });

    const today = moment();
    const isCurrentMonth =
      month === today.month() + 1 && year === today.year();

    const newSelectedDate = isCurrentMonth
      ? today.format("YYYY-MM-DD")
      : moment({ year, month: month - 1, date: 1 }).format("YYYY-MM-DD");

    setSelectedDate(newSelectedDate);
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.leftPanel}>
        <MyCalendar
          events={events}
          refreshEvents={fetchEvents}
          onDayClick={handleDayClick}
          empleado={empleado}
          isAdmin={isAdmin}
          onMonthChange={onMonthChange}
          onVisibleMonthChange={handleVisibleMonthChange}
          selectedDate={selectedDate}
        />
      </div>

      <div className={styles.rightPanel}>
        <ViewSelector />
        <div className={styles.headerContainer}>
          <TaskHeader
            selectedDate={selectedDate}
            onSuccess={handleTaskSuccess}
            userName={userName}
            empleado={empleado}
            proyectosTodos={proyectosTodos}
          />
        </div>

        {selectedTasks.length > 0 &&
          (notificationMessage ? (
            <div className={styles.notificationBanner}>
              <div className={styles.iconContainer}>
                <img src={FormCheckIcon} alt="Check icon" className={styles.iconCheck} />
              </div>
              {notificationMessage}
              <button className={styles.closeButton} onClick={handleCloseBanner}>
                <img src={CloseIcon} alt="Close icon" className={styles.closeIcon} />
              </button>
            </div>
          ) : (
            <>
            <TimeBar tasks={selectedTasks} selectedDate={selectedDate} />
            </>
          ))}

        <div className={styles.timebarDivider} />

        <div className={styles.notesContainer}>
          <Notes initialNotes={userNotes} userId={empleado} />
        </div>

        <div className={styles.contentContainer}>
          {isLoading ? (
            <SpinnerComponent />
          ) : (
            <div className={styles.taskListContainer}>
              {selectedTasks.length > 0 ? (
                <OverlayScrollbarsComponent
                  options={{
                    scrollbars: {
                      autoHide: "scroll",
                      autoHideDelay: 300,
                    },
                  }}
                  style={{ maxHeight: "100%", width: "100%" }}
                >
                  <div className={styles.taskList}>
                    {sortedSelectedTasks.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        updateTaskInState={updateTaskInState}
                        onSuccess={handleTaskSuccess}
                        empleado={empleado}
                        proyectosTodos={proyectosTodos}
                      />
                    ))}
                  </div>
                </OverlayScrollbarsComponent>
              ) : (
                <div className={styles.noTaskContainer}>
                  <img src={foldersImg} alt="No tasks" className={styles.noTaskImage} />
                  <p className={styles.noTaskText}>Aún no tienes tareas asignadas</p>
                  <Button
                    variant="dark"
                    onClick={() => setModalIsOpen(true)}
                    rightIcon={PlusIcon}
                    rightIconAlt="Añadir tarea"
                    disabled={isPastDate}
                  >
                    Añadir tarea
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <TaskCreationModal
        isOpen={modalIsOpen}
        onClose={() => setModalIsOpen(false)}
        onSuccess={handleTaskSuccess}
        defaultDate={selectedDate}
        empleado={empleado}
        proyectosTodos={proyectosTodos}
      />
    </div>
  );
};

export default Main;
